import { useEffect, useReducer, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash/lang";
import { cloneDeep } from "lodash";
import Autocomplete from '@mui/material/Autocomplete';
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import i18n from "@plannel/utils/i18n";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import auditService from "@plannel/services/system/audit-service";
import AuditDifference from "./AuditDifference";
import AuditChangesCell from "./AuditChangesCell";
import { formatAuditTimestamp } from "@plannel/pages/utils/audit-diff-util";
import { TextField } from "@mui/material";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import codeUtil from "@plannel/pages/utils/code-util";
import { FS } from "@plannel/utils/font-util";

const { snackBarMessageColor, auditOperationColor, tintChipSx } = ColorUtil;
const FILTER_START_PERIOD = "filterStartPeriod";
const FILTER_END_PERIOD = "filterEndPeriod";
const FILTER_ENTITY_ID = "filterEntityId";
const FILTER_OPERATION_TYPES = "filterOperationType";
const FILTER_ENTITY_NAMES = "filterEntityName";
const FILTER_USERS = "filterUser";
const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const INITIAL_START_DATE = new ZDate(Date.now() - 7 * 24 * 60 * 60 * 1000);
const INITIAL_END_DATE = CURRENT_DATE;
const OPERATION_TYPE_LIST = ["INSERT", "UPDATE", "DELETE"];
const REDUX_KEY = "AUDIT_TRAIL_DETAIL";
// 필터 컨트롤 폭 — 날짜·드롭다운을 한 값으로 묶어 툴바가 들쭉날쭉해 보이지 않게 한다.
const FILTER_WIDTH = 180;

const INITIAL_SEARCH_CONDITION = {
  period: { startDate: INITIAL_START_DATE, endDate: INITIAL_END_DATE },
  entityId: "",
  operationType: null,
  entityName: null,
  user: null,
}

const CACHE_SEARCH_CONDITION = (filter) => {
  return {
    ...filter,
    period: {
      startDate: filter.period.startDate ? new ZDate(filter.period.startDate) : null,
      endDate: filter.period.endDate ? new ZDate(filter.period.endDate) : null
    }
  }
}

const reducer = (state, action) => {
  let value = action.value;
  switch (action.type) {
    case FILTER_OPERATION_TYPES:
      return {
        ...state, operationType: value
      }
    case FILTER_ENTITY_NAMES:
      return {
        ...state, entityName: value
      }
    case FILTER_ENTITY_ID:
      return {
        ...state, entityId: value
      }
    case FILTER_USERS:
      return {
        ...state, user: value
      }
    case FILTER_START_PERIOD:
      if (value < START_MIN_DATE) {
        value = START_MIN_DATE;
      }
      if (value > state.period.endDate) {
        value = state.period.endDate;
      }

      return {
        ...state, period: { ...state.period, startDate: value }
      }
    case FILTER_END_PERIOD:
      if (value > INITIAL_END_DATE) {
        value = INITIAL_END_DATE;
      }
      if (value < state.period.startDate) {
        return {
          ...state, period: { ...state.period, startDate: value, endDate: value }
        }
      }

      return {
        ...state, period: { ...state.period, endDate: value }
      }
    default: return {
      ...state
    }
  }
}

const AuditTrail = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const pageSizes = [100, 200, 500];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.page ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [entityNameList, setEntityNameList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selectedSearchCons, dispatch] = useReducer(reducer, reduxViewState?.filter ? CACHE_SEARCH_CONDITION(reduxViewState.filter) : INITIAL_SEARCH_CONDITION);
  const entityId = useRef(reduxViewState?.filter.entityId ?? "");
  const [sortParams, setSortParams] = useState({})

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [auditLogs, setAuditLogs] = useState([]);

  const [auditDifferenceOpen, setAuditDifferenceOpen] = useState(false);
  const [selectedRowDiff, setSelectedRowDiff] = useState();

  const { auditEventType, auditEntityNms } = codeUtil;

  const processEntityNames = (rawNames) => {
    const map = new Map();
    rawNames.forEach(name => {
      const display = auditEntityNms?.[name] ?? name;
      if (!map.has(display)) {
        map.set(display, { display, auditLog: null, auditTrail: null });
      }
      const entry = map.get(display);
      if (name.startsWith('z_')) {
        entry.auditTrail = name;
      } else {
        entry.auditLog = name;
      }
    });
    return Array.from(map.values()).sort((a, b) => a.display.localeCompare(b.display));
  };

  const cellClicked = (params) => {
    const { value } = params;
    const newEntityId = String(value);
    const pageState = reduxUtil.createObjectState(REDUX_KEY, { currentPage: 1, pageSize: 100 });
    const filter = cloneDeep(selectedSearchCons);
    filter.entityId = newEntityId;
    filter.period.startDate = null;
    filter.period.endDate = null;
    const filterState = reduxUtil.createFilterViewState(REDUX_KEY, filter);
    const reduxState = { ...pageState, ...filterState };

    reduxDispatch(reduxUtil.updateViewState(reduxState));
    reduxDispatch(reduxUtil.updateHistoryState(REDUX_KEY));
  }

  const cellRenderer = (params) => {
    const { value } = params;

    if (viewName === REDUX_KEY) {
      return value.toString();
    }
    // 상세 화면(AUDIT_TRAIL_DETAIL)은 사이드바에 없는 링크 전용 화면이다(TabMenuList hide: true).
    //   isMenuPermitted 는 hide 를 보지 않으므로, 권한만 맞으면 그대로 링크가 된다.
    if (!isPermitted(REDUX_KEY)) return value.toString();
    return (
      <Box
        sx={{ color: "primary.main", textDecoration: "underline", cursor: "pointer" }}
        onClick={() => cellClicked(params)}
        role="button"
        tabIndex={0}>
        {value.toString()}
      </Box>
    )
  }

  // 이벤트 타입은 값이 셋뿐이고 행마다 반복되므로, 글자 대신 색으로 먼저 읽히게 칩으로 둔다.
  const operationTypeRenderer = (params) => {
    const { value } = params;
    if (isEmpty(value)) {
      return null;
    }

    const label = auditEventType?.[value] ? t(auditEventType[value]) : value;
    return <Chip size="small" label={label} sx={{ height: 20, fontSize: FS.SUB, ...tintChipSx(auditOperationColor[value]) }} />;
  }

  const entityNameValueFormatter = (params) => {
    const { value } = params;
    const name = auditEntityNms[value];
    if (!name) {
      return value;
    }

    return name;
  }

  const openDifference = (rowData) => {
    if (isEmpty(rowData)) {
      return;
    }

    setSelectedRowDiff(rowData);
    setAuditDifferenceOpen(true);
  }

  /*
    oldValue / newValue 원본 JSON 컬럼은 걷어내고 "변경 내역" 한 컬럼으로 대체했다.
    원본은 한 줄에 담기지 않아 목록에서는 읽히지 않았고(무엇이 바뀌었는지는 결국 팝업에서 확인),
    같은 폭이면 diff 요약이 훨씬 많은 정보를 준다. 원본 전체는 상세 팝업이 담당한다.
  */
  const columnDefs = [
    { headerName: "entityId", field: "entityId", colId: "entityId", type: ["nonEditableColumn", "defaultValueFormatter"], width: 180, cellRenderer: (params) => cellRenderer(params) },
    { headerName: "eventType", field: "operationType", colId: "operationType", type: "nonEditableColumn", width: 120, cellRenderer: (params) => operationTypeRenderer(params) },
    { headerName: "entityName", field: "entityName", colId: "entityName", type: "nonEditableColumn", width: 170, valueFormatter: entityNameValueFormatter },
    { headerName: "user", field: "createdUser", colId: "createdUser", type: "nonEditableColumn", width: 170 },
    { ...GridUtils.composedHeader(() => `${i18n.t("createdTs")}(UTC)`), field: "createdTs", colId: "createdTs", type: "nonEditableColumn", width: 160, valueFormatter: (params) => formatAuditTimestamp(params.value) },
    {
      headerName: "changes", field: "diffValue", colId: "changes", type: "nonEditableColumn",
      flex: 1, minWidth: 320, sortable: false, cellRenderer: AuditChangesCell,
      onCellClicked: (params) => openDifference(params.data),
    },
  ];

  const getRequestParams = () => {
    const params = {};

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    if (resultTotalCount.current) {
      params.totalCount = resultTotalCount.current;
    }
    params.startDate = selectedSearchCons.period.startDate;
    params.endDate = selectedSearchCons.period.endDate;
    params.operationType = selectedSearchCons.operationType;
    params.entityId = entityId.current;
    params.auditLogEntityName = selectedSearchCons.entityName?.auditLog ?? null;
    params.auditTrailEntityName = selectedSearchCons.entityName?.auditTrail ?? null;
    params.user = selectedSearchCons.user;
    return { ...params, ...sortParams };
  }

  const retrieveAuditLogs = () => {
    setAuditLogs([]);

    const params = getRequestParams();
    auditService.getAudit(params)
      .then(response => {
        const { results, totalPages, totalItems } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setAuditLogs(results);
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
      })
      .then(() => {
        selectedSearchCons.entityId = entityId.current;
        const reduxState = reduxUtil.createFilterViewState(viewName, selectedSearchCons);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  }

  const retrieveEntityNames = () => {
    auditService.getEntityNames()
      .then(response => {
        setEntityNameList(processEntityNames(response.data.results));
      })
      .catch(error => { debug(error); });
  }

  const retrieveUserList = () => {
    auditService.getUserList()
      .then(response => {
        const userList = response.data.results.sort();
        setUserList(userList);
      })
      .catch(error => { debug(error); });
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveAuditLogs();
    }
  }

  const onGridReady = params => {
    retrieveEntityNames();
    retrieveUserList();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveAuditLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSearchCons])

  const auditColDef = () => {
    const defaultSettings = DefaultGridSetting({ title, viewName });
    const mySetting = { ...defaultSettings, defaultColDef: { ...defaultSettings.defaultColDef } }

    return mySetting;
  }

  return (
    <>
      <FilterContainer hasIcons={false} sx={{ columnGap: 1 }}>
        {viewName === REDUX_KEY ? "" :
          <LocalizationProvider dateAdapter={AdapterDateFns} >
            {/* 시작~종료는 값 두 개가 아니라 기간 하나다 — 물결표로 묶어 한 덩어리로 읽히게 한다 */}
            <Box id="audit-trail-filter-date" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DatePicker
                label={t("periodStart")}
                views={['year', 'month', 'day']}
                format={'yyyy-MM-dd'}
                value={selectedSearchCons.period.startDate}
                onAccept={newValue => dispatch({ type: FILTER_START_PERIOD, value: newValue })}
                slotProps={{ textField: { size: "small", sx: { width: FILTER_WIDTH } } }}
              />
              <Box sx={{ color: "text.disabled" }}>~</Box>
              <DatePicker
                label={t("periodEnd")}
                views={['year', 'month', 'day']}
                format={'yyyy-MM-dd'}
                value={selectedSearchCons.period.endDate}
                onAccept={newValue => dispatch({ type: FILTER_END_PERIOD, value: newValue })}
                slotProps={{ textField: { size: "small", sx: { width: FILTER_WIDTH } } }}
              />
            </Box>
          </LocalizationProvider>
        }
        {viewName === REDUX_KEY ? <TextFieldSearch sx={{ width: 220 }} label={"entityId"} searchCode={entityId} retrieve={initPageToRetrieve} /> : ""}
        <Autocomplete
          sx={{ width: FILTER_WIDTH }}
          getOptionLabel={option => auditEventType?.[option] ? t(auditEventType[option]) : option}
          options={OPERATION_TYPE_LIST}
          value={selectedSearchCons?.operationType}
          size="small"
          onChange={(_, newValue) => dispatch({ type: FILTER_OPERATION_TYPES, value: newValue })}
          renderInput={(params) => (
            <TextField {...params} label={t("eventType")} />
          )}
        />
        <Autocomplete
          sx={{ width: FILTER_WIDTH }}
          options={entityNameList}
          getOptionLabel={option => option.display}
          isOptionEqualToValue={(option, value) => option.display === value.display}
          value={selectedSearchCons.entityName}
          size="small"
          onChange={(_, newValue) => dispatch({ type: FILTER_ENTITY_NAMES, value: newValue })}
          renderInput={(params) => (
            <TextField {...params} label={t("entityName")} />
          )}
        />
        <Autocomplete
          sx={{ width: FILTER_WIDTH }}
          options={userList}
          value={selectedSearchCons.user}
          size="small"
          onChange={(_, newValue) => dispatch({ type: FILTER_USERS, value: newValue })}
          renderInput={(params) => (
            <TextField {...params} label={t("user")} />
          )}
        />
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1} >
        <AgGridReact
          ref={gridRef}
          rowData={auditLogs}
          {...auditColDef()}
          rowSelection={{ mode: 'singleRow', checkboxes: false, headerCheckbox: false, enableClickSelection: true }}
          tooltipShowDelay={0}
          tooltipHideDelay={20000}
          onGridReady={onGridReady}
          onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
          onRowDoubleClicked={params => openDifference(params.data)}
        >
        </AgGridReact>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <AuditDifference
        open={auditDifferenceOpen}
        close={setAuditDifferenceOpen}
        selectedData={selectedRowDiff}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};
export default withTranslation()(AuditTrail);
